import React, { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import { getFreshToken } from "../api";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");

/**
 * One-time Terms & Disclaimer acceptance gate.
 * Blocks the app on first login (per terms version) until the user checks
 * both boxes and accepts. Acceptance is recorded server-side for audit.
 */
export default function TermsGate({ idToken, onAccepted }) {
  const [phase, setPhase] = useState("checking"); // checking | blocked | done
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeDisclaimer, setAgreeDisclaimer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const MAX_ATTEMPTS = 5;
    // Block ONLY on a SUCCESSFUL status that says accepted:false. Transient failures (a 401
    // while the session token is refreshing on a cold reload, or a brief 5xx) are retried a
    // BOUNDED number of times with backoff — so an already-accepted user isn't re-prompted by
    // a blip, and the gate can NEVER spin forever. If the status can't be read after all
    // attempts, we FAIL OPEN (let the user into the app) rather than trap them on a spinner or
    // wrongly show the acceptance modal — the app's own auth handling covers a truly dead
    // session. First attempt uses the passed token (fast, avoids a session fetch that could
    // hang); later attempts refresh the token.
    async function resolveToken(attempt) {
      if (attempt === 0 && idToken) return idToken;
      try { return (await getFreshToken()) || idToken; } catch (e) { return idToken; }
    }
    async function check(attempt = 0) {
      if (cancelled) return;
      const failOrRetry = () => {
        if (cancelled) return;
        if (attempt + 1 < MAX_ATTEMPTS) setTimeout(() => check(attempt + 1), Math.min(1200 * (attempt + 1), 5000));
        else { setPhase("done"); onAccepted && onAccepted(); }  // fail open — never trap the user
      };
      let token;
      try { token = await resolveToken(attempt); } catch (e) { token = idToken; }
      if (!token) { failOrRetry(); return; }
      try {
        const res = await fetch(`${API_URL}/user/terms-status`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { failOrRetry(); return; }
        const data = await res.json().catch(() => null);
        if (cancelled) return;
        if (data && data.accepted) { setPhase("done"); onAccepted && onAccepted(); }
        else if (data) { setPhase("blocked"); }   // confirmed "not accepted" → require acceptance
        else { failOrRetry(); }                    // unparseable → retry / fail open
      } catch (e) {
        failOrRetry();
      }
    }
    check();
    return () => { cancelled = true; };
  }, [idToken, onAccepted]);

  const accept = async () => {
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/user/accept-terms`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Could not record your acceptance. Please try again.");
      setPhase("done");
      onAccepted && onAccepted();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "done") return null;

  const overlay = {
    position: "fixed", inset: 0, zIndex: 100000,
    background: "rgba(8,12,24,0.72)", backdropFilter: "blur(5px)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
  };

  if (phase === "checking") {
    return (
      <div style={overlay}>
        <Loader2 size={26} color="#fff" className="animate-spin" />
      </div>
    );
  }

  const cardWrap = { background: "#fff", borderRadius: "16px", width: "100%", maxWidth: "560px", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 30px 80px rgba(0,0,0,.4)", overflow: "hidden" };
  const header = { padding: "24px 28px 16px", borderBottom: "1px solid #eef1f6" };
  const body = { padding: "20px 28px", overflowY: "auto", color: "#475569", fontSize: "14px", lineHeight: 1.6 };
  const footer = { padding: "18px 28px 24px", borderTop: "1px solid #eef1f6" };
  const checkRow = { display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "14px", cursor: "pointer", fontSize: "13.5px", color: "#1e293b" };

  return (
    <div style={overlay}>
      <div style={cardWrap}>
        <div style={header}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
            <ShieldCheck size={22} color="#6366f1" />
            <h2 style={{ margin: 0, fontSize: "19px", color: "#0f172a", letterSpacing: "-.3px" }}>Before you continue</h2>
          </div>
          <p style={{ margin: 0, fontSize: "13.5px", color: "#64748b" }}>Please review and accept the terms below to use Boomerang.</p>
        </div>

        <div style={body}>
          <p style={{ marginBottom: "14px" }}>
            <strong>Results depend on your data.</strong> Boomerang cleans, connects, and analyzes the files
            you upload. The accuracy and usefulness of any insight, chart, or report depends entirely on the
            quality, completeness, and accuracy of the data you provide.
          </p>
          <div style={{ display: "flex", gap: "10px", padding: "12px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "10px", marginBottom: "16px" }}>
            <AlertTriangle size={18} color="#b45309" style={{ flexShrink: 0, marginTop: "1px" }} />
            <span style={{ fontSize: "13px", color: "#92400e" }}>
              Boomerang's outputs are for informational purposes only and are <strong>not</strong> professional,
              financial, legal, or business advice. You are responsible for verifying results before acting on them.
            </span>
          </div>
          <p style={{ marginBottom: "10px" }}>By continuing you acknowledge that:</p>
          <ul style={{ paddingLeft: "20px", marginBottom: "4px", display: "flex", flexDirection: "column", gap: "8px" }}>
            <li>You have the right to upload and analyze the data you provide.</li>
            <li>Insights are generated from your data and may contain errors or omissions.</li>
            <li>Boomerang is not liable for decisions, losses, or damages arising from use of the results.</li>
            <li>The service is provided "as is," without warranties of any kind.</li>
          </ul>
        </div>

        <div style={footer}>
          {error && <div style={{ padding: "10px 12px", background: "#fee2e2", color: "#991b1b", borderRadius: "8px", marginBottom: "12px", fontSize: "13px" }}>{error}</div>}
          <label style={checkRow}>
            <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} style={{ marginTop: "2px", width: "16px", height: "16px", cursor: "pointer" }} />
            <span>I have read and agree to the <strong>Terms of Service</strong> and <strong>Privacy Policy</strong>.</span>
          </label>
          <label style={checkRow}>
            <input type="checkbox" checked={agreeDisclaimer} onChange={(e) => setAgreeDisclaimer(e.target.checked)} style={{ marginTop: "2px", width: "16px", height: "16px", cursor: "pointer" }} />
            <span>I understand that insights <strong>depend on the quality of my data</strong>, and that Boomerang is <strong>not responsible</strong> for decisions made based on the results.</span>
          </label>
          <button
            onClick={accept}
            disabled={!agreeTerms || !agreeDisclaimer || submitting}
            style={{
              width: "100%", marginTop: "8px", padding: "12px", borderRadius: "10px", border: "none",
              fontWeight: 700, fontSize: "15px", cursor: (!agreeTerms || !agreeDisclaimer || submitting) ? "not-allowed" : "pointer",
              color: "#fff",
              background: (!agreeTerms || !agreeDisclaimer || submitting) ? "#94a3b8" : "linear-gradient(135deg, #6366f1, #7c3aed)",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            {submitting ? "Recording…" : "I Agree & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
